import moment from "moment";
import { getVueAppBaseApi } from "./util";

/**
 * 防抖 在事件被触发一定事件后再执行回调，如果在这段事件内又被触发，则重新计时
 * 使用场景：
 * 1、搜索框中，用户在不断输入值时，用防抖来节约请求资源
 * 2、window触发resize的时候，不断的调整浏览器窗口大小会不断的触发这个事件，用防抖来让其只触发一次
 * @param fn 回调
 * @param delay 时间间隔的阈值(单位：ms) 默认500ms
 */
export function debounce(fn: Function, delay = 500) {
  let timer: number;
  // eslint-disable-next-line func-names
  return function (this: void, ...args: any) {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

/**
 * 节流 规定在一段时间内，只能触发一次函数。如果这段时间内触发多次函数，只有一次生效
 * 使用场景：
 * 1、鼠标不断点击触发，mousedown(单位时间内只触发一次)
 * 2、监听滚动事件，比如是否滑到底部自动加载更多
 * @param fn 回调
 * @param delay 时间间隔的阈值(单位：ms) 默认500ms
 * @this throttle
 */
export function throttle(fn: Function, delay = 500) {
  // last为上一次触发回调的时间, timer是定时器
  let last = 0;
  let timer: any = null;
  // 将throttle处理结果当作函数返回
  // eslint-disable-next-line func-names
  return function (this: void) {
    // 保留调用时的this上下文
    const context = this;
    // 保留调用时传入的参数
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    // 记录本次触发回调的时间
    const now = +new Date();

    // 判断上次触发的时间和本次触发的时间差是否小于时间间隔的阈值
    if (now - last < delay) {
      // 如果时间间隔小于我们设定的时间间隔阈值，则为本次触发操作设立一个新的定时器
      clearTimeout(timer);
      timer = setTimeout(() => {
        last = now;
        fn.apply(context, args);
      }, delay);
    } else {
      // 如果时间间隔超出了我们设定的时间间隔阈值，那就不等了，无论如何要反馈给用户一次响应
      last = now;
      fn.apply(context, args);
    }
  };
}

/**
 * 判断是移动端还是 pc 端 ，true 表示是移动端，false 表示是 pc 端
 */
export function isMobileOrPc() {
  return /Android|webOS|iPhone|iPod|BlackBerry/i.test(navigator.userAgent);
}

/**
 * 判断
 * @param a 新对象
 * @param b 原对象
 */
export function isObjectValueEqualNew(a:any, b:any): boolean {
  if (!a || !b) {
    return a == b;
  }
  let aProps = Object.keys(a);
  let bProps = Object.keys(b);

  if (aProps.length != bProps.length) {
    return false;
  }
  for (let i = 0; i < aProps.length; i++) {
    let propName = aProps[i]
    let propA = a[propName]
    let propB = b[propName]
    if ((typeof propA === 'object')) {
      let status = isObjectValueEqualNew(propA, propB);
      if (!status) {
        return false;
      }
    } else if (propA !== propB) {
      return false
    }
  }
  return true
}

const options = [{
  "show": "今日",
  "en": "TODAY",
  end: new Date(),
  start: new Date(new Date().setHours(0, 0, 0, 0))
},
  {
    "show": "昨日",
    "en": "YESTERDAY",
    end: new Date(new Date().setHours(0, 0, 0, 0) - 1),
    start: new Date(new Date().setHours(0, 0, 0, 0) - 24 * 60 * 60 * 1000)
  },
  {
    "show": "本周",
    "en": "THISWEEK",
    end: new Date(),
    start: getWeekFirstDay()
  },
  {
    "show": "上周",
    "en": "LASTWEEK",
    end: getWeekFirstDay(-1),
    start: getWeekFirstDay(- 7 * 24 * 60 * 60 * 1000)
  },
  {
    "show": "最近7天",
    "en": "LAST7WEEK",
    end: new Date(),
    start: getLastNFirstDay(7),
  },
  {
    "show": "本月",
    "en": "THISMONTH",
    end: new Date(),
    start: getMonthFirstDay()
  },
  {
    "show": "上月",
    "en": "LASTMONTH",
    end: getMonthFirstDay(-1),
    start: getLastMonthFirstDay()
  },
  {
    "show": "最近30天",
    "en": "LAST30WEEK",
    end: new Date(),
    start: getLastNFirstDay(30),
  },
  {
    "show": "本年",
    "en": "THISYEAR",
    end: new Date(),
    start: getYearFirstDay()
  },
  {
    "show": "去年",
    "en": "LASTYEAR",
    end: getYearFirstDay(-1),
    start: getLastYearFirstDay()
  },
  // {
  //   "show": "过去$day天",
  //   "en": "LASTDAYS"
  // },
  // {
  //   "show": "上线至今",
  //   "en": "RIGHTNOW"
  // },
  // {
  //   "show": "绝对时间",
  //   "en": "ABSOLUTE"
  // },
  // {
  //   "show": "用户进入流程",
  //   "en": "INFLOW"
  // }
]

export function getPickerOptions() {
  return {
    shortcuts: options.map(option =>
      ({
        text: option.show,
        en: option.en,
        onClick(picker: any) {
          picker.activeShortcut = option.en;
          picker.$emit('pick', [option.start, option.end]);
        }
      })
    )
  }
}

export function getWeekFirstDay(deviation: number = 0) {
  const nowTemp = new Date();//当前时间
  const oneDayLong = 24 * 60 * 60 * 1000;//一天的毫秒数
  const c_time = nowTemp.getTime();//当前时间的毫秒时间
  const c_day = nowTemp.getDay() || 7;//当前时间的星期几
  const m_time = c_time - (c_day - 1) * oneDayLong;//当前周一的毫秒时间
  return new Date(new Date(m_time).setHours(0,0,0,0) + deviation);//周一时间对象
}
export function getMonthFirstDay(deviation: number = 0) {
  const date = new Date();
  const m_time = date.setDate(1);
  return new Date(new Date(m_time).setHours(0,0,0,0) + deviation);
}
export function getLastMonthFirstDay() {
  const date = new Date();
  let year = date.getFullYear();
  let month = date.getMonth();
  if (month === 0) {
    month = 11;
    year = year - 1;
  } else {
    month = month - 1;
  }
  return new Date(year, month, 1);
}
export function getYearFirstDay(deviation: number = 0) {
  const date = new Date();
  date.setMonth(0);
  const m_time = date.setDate(1);
  return new Date(new Date(m_time).setHours(0,0,0,0) + deviation);
}
export function getLastYearFirstDay() {
  const date = new Date();
  let year = date.getFullYear();
  return new Date(year - 1, 0,1);
}

// 获取n天前的日期
export function getLastNFirstDay(n: number) {
  return new Date(moment(new Date()).subtract(n, "days").format("YYYY-MM-DD HH:mm:ss"))
}

export function getDate() {
  const date = new Date();
  const year = date.getFullYear().toString();
  let month = (date.getMonth() + 1).toString();
  const day = date.getDate().toString();
  if (parseInt(month) < 10) {
    month = '0' + month;
  }
  return `${year}-${month}-${day}`;
}

export function dateFormat(fmt: any, date: Date) {
  let ret;
  const opt = {
    "Y+": date.getFullYear().toString(),        // 年
    "m+": (date.getMonth() + 1).toString(),     // 月
    "d+": date.getDate().toString(),            // 日
    "H+": date.getHours().toString(),           // 时
    "M+": date.getMinutes().toString(),         // 分
    "S+": date.getSeconds().toString()          // 秒
    // 有其他格式化字符需求可以继续添加，必须转化成字符串
  };
  for (let k in opt) {
    ret = new RegExp("(" + k + ")").exec(fmt);
    if (ret) {
      // @ts-ignore
      fmt = fmt.replace(ret[1], (ret[1].length == 1) ? (opt[k]) : (opt[k].padStart(ret[1].length, "0")))
    };
  };
  return fmt;
}

export function UTCDateFormat(fmt: any, date: Date) {
  let ret;
  const opt = {
    "Y+": date.getUTCFullYear().toString(),        // 年
    "m+": (date.getUTCMonth() + 1).toString(),     // 月
    "d+": date.getUTCDate().toString(),            // 日
    "H+": date.getUTCHours().toString(),           // 时
    "M+": date.getUTCMinutes().toString(),         // 分
    "S+": date.getUTCSeconds().toString()          // 秒
    // 有其他格式化字符需求可以继续添加，必须转化成字符串
  };
  for (let k in opt) {
    ret = new RegExp("(" + k + ")").exec(fmt);
    if (ret) {
      // @ts-ignore
      fmt = fmt.replace(ret[1], (ret[1].length == 1) ? (opt[k]) : (opt[k].padStart(ret[1].length, "0")))
    }
  }
  return fmt;
}

export function timezoneChange(value: string, oldTimezone: string = 'GMT+0800', newTimezone: string = 'GMT+0800', fmt: string = 'YYYY-mm-dd HH:MM:SS'): string {
  if (!value) {
    return value;
  }
  const valueDate = new Date(value.replace(/-/g, '/') + ' ' + oldTimezone);
  const timezoneInfo = newTimezone.match(new RegExp('(?:UTC|GMT)([+-]*[0-9]{1,2}):*([0-9]{2})'));
  if (!timezoneInfo) return value;
  const hour = parseInt(timezoneInfo[1]);
  const minute = hour >= 0 ? parseInt(timezoneInfo[2]) : -parseInt(timezoneInfo[2]);
  const newDate = new Date(valueDate.getTime() + hour * 60 * 60 * 1000 + minute * 60 * 1000);
  return UTCDateFormat(fmt,newDate);
}

export function handleExcelRes(res: any) {
  const blob = new Blob(
    [res.data], {type: 'application/vnd.ms-excel; chartset=utf-8'})
  const aEle = document.createElement('a');     // 创建a标签
  const href = window.URL.createObjectURL(blob);       // 创建下载的链接
  aEle.href = href;
  aEle.download = decodeURI(res.headers['content-disposition'].replace('attachment;filename=', ''));  // 下载后文件名
  document.body.appendChild(aEle);
  aEle.click();     // 点击下载
  document.body.removeChild(aEle); // 下载完成移除元素
  window.URL.revokeObjectURL(href) // 释放掉blob对象
}

/**
 * 下载文件
 * @param {String} path - 下载地址/下载请求地址。
 * @param {String} name - 下载文件的名字/重命名
 * @param {String} suffix - 文件名后缀
 */
export function downloadFile (path: string, name: string, suffix: string = '') {
  const xhr = new XMLHttpRequest();
  xhr.open('get', path);
  xhr.responseType = 'blob';
  xhr.send();
  xhr.onload = function () {
    if (this.status === 200 || this.status === 304) {
      // 如果是IE10及以上，不支持download属性，采用msSaveOrOpenBlob方法，但是IE10以下也不支持msSaveOrOpenBlob
      if ('msSaveOrOpenBlob' in navigator) {
        navigator.msSaveOrOpenBlob(this.response, name);
        return;
      }
      // const blob = new Blob([this.response], { type: xhr.getResponseHeader('Content-Type') || '' });
      // const url = URL.createObjectURL(blob);
      const url = URL.createObjectURL(this.response);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = suffix?  name + '.' + suffix : name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };
}

export function downloadFile2 (path: string, name: string, suffix: string = '') {
  // console.log('downloadFile2')
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = path;
  a.download = suffix?  name + '.' + suffix : name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**  
* 计算两个日期之间的天数  
* @param {string} Date_end 结束日期  
* @param {string} Date_start 开始日期  
* @return {number} iDays间隔的天数  
*/
export function DateDiff(Date_end:any, Date_start: any) {
  //将时间以空格划分为两个数组  第一个数组是 xxxx-xx-xx 第二个数组是 00：00：00
  const endDateArr = Date_end.split(" ")[0].split("-"); 
  const endDate: any = new Date(endDateArr[0], endDateArr[1], endDateArr[2]); 

  const startDateArr = Date_start.split(" ")[0].split("-");
  const startDate: any = new Date(startDateArr[0], startDateArr[1], startDateArr[2]);
  //把相差的毫秒数转换为天数
  return parseInt(String(Math.abs(endDate - startDate) / 1000 / 60 / 60 / 24)); 
}


/**
 * 探活接口 /health.json
 */
export function fetchHealthCheck() {
  return new Promise((resolve, reject) => {
    const startTime = new Date().getTime();

    fetch(`${getVueAppBaseApi()}/health.json`)
      .then((response) => response.json())
      .then((data) => {
        const endTime = new Date().getTime();
        const duration = endTime - startTime;

        resolve({
          code: data.code || 200,
          message: data.message || 'success',
          data: data.data || {},
          duration: duration // 请求耗时（毫秒）
        });
      })
      .catch((error) => {
        reject(error);
      });
  });
}
